// src/components/attendance/AttendanceTable.js
import { useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { attendanceService } from '../../services/attendanceService';
import { getAttendanceDurationMinutes, formatAttendanceTime } from '../../utils/dateUtils';

const AttendanceTable = ({
    attendance,
    loading,
    pagination,
    onPageChange,
    onExport,
    userType,
    onUpdate,
    onRefresh
}) => {

    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewRecord, setReviewRecord] = useState(null);
    const [reviewForm, setReviewForm] = useState({ sign_out_time: '', review_note: '', next_day: false });
    const [reviewSaving, setReviewSaving] = useState(false);

    const handleExportClick = (format) => {
        onExport(format);
    };

    const resolveMediaUrl = (path) => {
        const value = String(path || '').trim();
        if (!value) return '';
        if (/^https?:\/\//i.test(value) || value.startsWith('data:image/')) return value;

        const apiBase = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/+$/, '');
        const clean = value.replace(/^\/+/, '');
        return `${apiBase}/${clean}`;
    };

    const groupAttendanceByStaffAndDate = (records = []) => {
        const grouped = {};

        records.forEach((record) => {
            const key = `${record.user_id || 'nouser'}_${record.date || 'nodate'}`;
            if (!grouped[key]) {
                grouped[key] = {
                    ...record,
                    entries: []
                };
            }

            grouped[key].entries.push({
                id: record.id,
                sign_in_time: record.sign_in_time,
                sign_out_time: record.sign_out_time,
                attendance_type: record.attendance_type,
                reason: record.reason || ''
            });

            if (!grouped[key].review_target_id && record.reason) {
                grouped[key].review_target_id = record.id;
                grouped[key].review_reason = record.reason;
            }
        });

        Object.values(grouped).forEach((item) => {
            const hasReason = item.entries.some((entry) => String(entry.reason || '').trim() !== '');
            item.has_review_reason = hasReason;
            if (!item.review_target_id && item.entries.length > 0) {
                item.review_target_id = item.entries[item.entries.length - 1].id;
            }
        });

        return Object.values(grouped);
    };

    const attendanceRows = groupAttendanceByStaffAndDate(attendance || []);

    // Review button hidden on the Attendance Management page per request -
    // underlying review flow (modal, handlers) left intact in case it's
    // re-enabled later.
    const canReview = false;

    const handleReviewClick = (record) => {
        let targetEntry = null;
        if (record?.review_target_id) {
            targetEntry = (record.entries || []).find((entry) => Number(entry.id) === Number(record.review_target_id)) || null;
        }
        if (!targetEntry && Array.isArray(record?.entries) && record.entries.length > 0) {
            targetEntry = record.entries[record.entries.length - 1];
        }

        setReviewRecord({
            ...record,
            attendance_id: targetEntry?.id || null,
            sign_in_time: targetEntry?.sign_in_time || null
        });
        const rawSignOut = String(targetEntry?.sign_out_time || '');
        const signOutTimePart = rawSignOut.includes(' ') ? rawSignOut.split(' ')[1] : rawSignOut;
        // Auto-detect overnight: if stored sign_out date differs from sign_in date,
        // pre-check "next day" so the admin doesn't have to remember to tick it.
        const rawSignIn = String(targetEntry?.sign_in_time || '');
        const signInDatePart = rawSignIn.includes(' ') ? rawSignIn.split(' ')[0] : (targetEntry?.date || '');
        const signOutDatePart = rawSignOut.includes(' ') ? rawSignOut.split(' ')[0] : '';
        const isOvernight = signOutDatePart !== '' && signInDatePart !== '' && signOutDatePart !== signInDatePart;
        setReviewForm({
            sign_out_time: signOutTimePart.slice(0, 5),
            review_note: '',
            next_day: isOvernight
        });
        setShowReviewModal(true);
    };

    const handleReviewSubmit = async () => {
        if (!reviewRecord?.attendance_id) {
            alert('No attendance entry selected for review');
            return;
        }
        if (!reviewForm.sign_out_time) {
            alert('Please provide a sign out time');
            return;
        }

        try {
            setReviewSaving(true);
            await attendanceService.reviewAttendance(reviewRecord.attendance_id, {
                sign_out_time: reviewForm.sign_out_time,
                review_note: reviewForm.review_note,
                next_day: reviewForm.next_day
            });
            setShowReviewModal(false);
            setReviewRecord(null);
            if (typeof onRefresh === 'function') {
                await onRefresh();
            }
            alert('Attendance reviewed successfully');
        } catch (err) {
            alert(err.message || 'Failed to review attendance');
        } finally {
            setReviewSaving(false);
        }
    };

    const handleViewDetails = (record) => {
        setSelectedRecord(record);
        setShowDetailsModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = formatAttendanceTime;

    const calculateWorkingHours = (signInTime, signOutTime) => {
        if (!signInTime || !signOutTime) return '-';

        const diffMinutes = getAttendanceDurationMinutes(signInTime, signOutTime);
        if (diffMinutes === null) return 'Invalid';

        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        return `${hours}h ${minutes}m`;
    };

    const getStatusBadge = (status, signIn, signOut, actualStatus) => {
        let statusClass = 'present';
        let statusText = 'Present';
        let icon = 'fa-check-circle';

        // Use actual_status from shift calculation if available
        if (status === 'holiday' || actualStatus === 'holiday') {
            statusClass = 'holiday';
            statusText = 'Holiday';
            icon = 'fa-calendar-check';
        } else if (actualStatus === 'late') {
            statusClass = 'late';
            statusText = 'Late';
            icon = 'fa-clock';
        } else if (actualStatus === 'within_margin') {
            statusClass = 'within-margin';
            statusText = 'Within Margin';
            icon = 'fa-clock';
        } else if (actualStatus === 'on_time') {
            statusClass = 'on-time';
            statusText = 'On Time';
            icon = 'fa-check-circle';
        } else if (status === 'absent' || (!signIn && !signOut)) {
            statusClass = 'absent';
            statusText = 'Absent';
            icon = 'fa-times-circle';
        } else if (status === 'late') {
            statusClass = 'late';
            statusText = 'Late';
            icon = 'fa-clock';
        } else if (status === 'on_leave') {
            statusClass = 'on-leave';
            statusText = 'On Leave';
            icon = 'fa-umbrella-beach';
        }

        return (
            <span className={`status-badge ${statusClass}`}>
                <i className={`fas ${icon}`}></i>
                {statusText}
            </span>
        );
    };

    const formatShiftTime = (timeString) => {
        if (!timeString) return '-';
        // timeString is in HH:MM:SS format
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const formatTimeDifference = (timeDiff) => {
        if (!timeDiff) return null;

        // timeDiff is in HH:MM:SS format, can be negative
        const isNegative = timeDiff.startsWith('-');
        const absTime = timeDiff.replace('-', '');
        const [hours, minutes] = absTime.split(':');

        const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);

        if (totalMinutes === 0) return null;

        if (isNegative) {
            return (
                <span className="time-diff early">
                    <i className="fas fa-arrow-down"></i>
                    {totalMinutes}m early
                </span>
            );
        } else {
            return (
                <span className="time-diff late">
                    <i className="fas fa-arrow-up"></i>
                    {totalMinutes}m late
                </span>
            );
        }
    };

    const getAttendanceTypeDisplay = (attendanceType) => {
        const type = String(attendanceType || '').toLowerCase();
        if (type === 'offsite' || type === 'off_site') {
            return { label: 'Offsite', className: 'offsite' };
        }
        return { label: 'In-Premises', className: 'in-premises' };
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const { current_page, total_pages } = pagination;

        // Previous button
        buttons.push(
            <button
                key="prev"
                className="pagination-btn"
                onClick={() => onPageChange(current_page - 1)}
                disabled={current_page <= 1}
                title="Previous Page"
            >
                <i className="fas fa-chevron-left"></i>
            </button>
        );

        // Page numbers with ellipsis
        const startPage = Math.max(1, current_page - 2);
        const endPage = Math.min(total_pages, current_page + 2);

        if (startPage > 1) {
            buttons.push(
                <button
                    key={1}
                    className="pagination-btn"
                    onClick={() => onPageChange(1)}
                >
                    1
                </button>
            );
            if (startPage > 2) {
                buttons.push(
                    <span key="ellipsis1" className="pagination-ellipsis">...</span>
                );
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    className={`pagination-btn ${i === current_page ? 'active' : ''}`}
                    onClick={() => onPageChange(i)}
                >
                    {i}
                </button>
            );
        }

        if (endPage < total_pages) {
            if (endPage < total_pages - 1) {
                buttons.push(
                    <span key="ellipsis2" className="pagination-ellipsis">...</span>
                );
            }
            buttons.push(
                <button
                    key={total_pages}
                    className="pagination-btn"
                    onClick={() => onPageChange(total_pages)}
                >
                    {total_pages}
                </button>
            );
        }

        // Next button
        buttons.push(
            <button
                key="next"
                className="pagination-btn"
                onClick={() => onPageChange(current_page + 1)}
                disabled={current_page >= total_pages}
                title="Next Page"
            >
                <i className="fas fa-chevron-right"></i>
            </button>
        );

        return buttons;
    };

    const handleExportDetailsPDF = () => {
        if (!selectedRecord) return;

        const user = JSON.parse(localStorage.getItem('haazri_user') || '{}');
        const recordOrgId = selectedRecord.organization_id || selectedRecord.org_id || user?.org_id;
        const appLogo = localStorage.getItem('app_logo') || '';
        const orgLogo = recordOrgId ? (localStorage.getItem(`org_logo_${recordOrgId}`) || '') : '';
        const rawLogoUrl = orgLogo || appLogo;
        const apiBase = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/+$/, '');
        const logoUrl = (() => {
            const value = String(rawLogoUrl || '').trim();
            if (!value) return '';
            if (/^https?:\/\//i.test(value) || value.startsWith('data:image/')) return value;
            if (value.startsWith('/')) return `${apiBase}${value}`;
            return `${apiBase}/${value}`;
        })();

        const esc = (val) => String(val ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');

        const organizationName = selectedRecord.organization_name || user?.org_name || '-';
        const staffName = selectedRecord.staff_name || selectedRecord.username || '-';
        const entries = (selectedRecord.entries || [])
            .slice()
            .sort((a, b) => String(a.sign_in_time || '').localeCompare(String(b.sign_in_time || '')));

        let totalMinutes = 0;
        entries.forEach((entry) => {
            const minutes = getAttendanceDurationMinutes(entry.sign_in_time, entry.sign_out_time);
            if (minutes !== null) totalMinutes += minutes;
        });
        const dayTotal = totalMinutes > 0 ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m` : '-';

        const rows = entries.map((entry, idx) => `
            <tr>
                <td>${idx + 1}</td>
                <td>${esc(formatTime(entry.sign_in_time))}</td>
                <td>${esc(formatTime(entry.sign_out_time))}</td>
                <td>${esc(calculateWorkingHours(entry.sign_in_time, entry.sign_out_time))}</td>
                <td>${esc(entry.attendance_type || 'Standard')}</td>
            </tr>
        `).join('');

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups to export PDF');
            return;
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Attendance Details - ${esc(selectedRecord.staff_name || selectedRecord.username || 'Staff')}</title>
                <style>
                    @page { size: A4; margin: 12mm; }
                    body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; padding: 0; }
                    .wrap { padding: 8px; }
                    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
                    .brand { display: flex; align-items: center; gap: 10px; }
                    .brand img { height: 50px; max-width: 220px; object-fit: contain; }
                    .fallback { font-size: 22px; font-weight: 800; color: #0f172a; }
                    .company { display: flex; flex-direction: column; gap: 2px; font-size: 11px; color: #475569; }
                    .company .org { font-weight: 700; font-size: 13px; color: #0f172a; }
                    .meta-top { text-align: right; }
                    .report-title { margin: 0; font-size: 20px; font-weight: 700; color: #0f172a; }
                    .report-date { margin: 3px 0 0; font-size: 12px; color: #64748b; }
                    .divider { border: 0; border-top: 2px solid #e2e8f0; margin: 6px 0 10px; }
                    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 10px; }
                    .card { border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; padding: 8px; }
                    .card .lbl { font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700; }
                    .card .val { font-size: 14px; font-weight: 800; margin-top: 2px; color: #0f172a; }
                    .meta { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; margin-bottom: 10px; background: #fff; }
                    .meta-row { margin: 4px 0; font-size: 12px; color: #334155; }
                    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
                    th, td { border: 1px solid #e5e7eb; padding: 7px; font-size: 11px; text-align: left; word-wrap: break-word; }
                    th { background: #f1f5f9; color: #1e293b; font-weight: 700; }
                </style>
            </head>
            <body>
                <div class="wrap">
                    <div class="header">
                        <div class="brand">
                            ${logoUrl ? `<img src="${esc(logoUrl)}" alt="Logo" onerror="this.style.display='none';this.nextElementSibling.style.display='block';" /><div class="fallback" style="display:none;">eHAAZRI</div>` : '<div class="fallback">eHAAZRI</div>'}
                            <div class="company">
                                <div class="org">${esc(organizationName)}</div>
                                <div>Attendance Management System</div>
                            </div>
                        </div>
                        <div class="meta-top">
                            <h1 class="report-title">Attendance Details Report</h1>
                            <p class="report-date">Generated: ${esc(new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }))}</p>
                        </div>
                    </div>
                    <hr class="divider" />
                    <div class="summary">
                        <div class="card"><div class="lbl">Staff</div><div class="val">${esc(staffName)}</div></div>
                        <div class="card"><div class="lbl">Date</div><div class="val">${esc(formatDate(selectedRecord.date))}</div></div>
                        <div class="card"><div class="lbl">Entries</div><div class="val">${entries.length}</div></div>
                        <div class="card"><div class="lbl">Day Total</div><div class="val">${esc(dayTotal)}</div></div>
                    </div>
                    <div class="meta">
                        <div class="meta-row"><strong>Organization:</strong> ${esc(organizationName)}</div>
                        <div class="meta-row"><strong>Premises:</strong> ${esc(selectedRecord.premises_name || '-')}</div>
                        <div class="meta-row"><strong>Shift:</strong> ${esc(selectedRecord.shift_name || 'No Shift Assigned')}</div>
                    </div>
                    <table>
                        <thead>
                            <tr><th>#</th><th>Check In</th><th>Check Out</th><th>Duration</th><th>Type</th></tr>
                        </thead>
                        <tbody>
                            ${rows || '<tr><td colspan="5" style="text-align:center;">No entries</td></tr>'}
                        </tbody>
                    </table>
                </div>
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
        printWindow.onload = () => {
            const logoImg = printWindow.document.querySelector('.brand img');
            const safePrint = () => {
                setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                }, 120);
            };

            if (!logoImg) {
                safePrint();
                return;
            }

            if (logoImg.complete && logoImg.naturalWidth > 0) {
                safePrint();
                return;
            }

            let done = false;
            const finalize = () => {
                if (done) return;
                done = true;
                safePrint();
            };

            logoImg.onload = finalize;
            logoImg.onerror = finalize;
            setTimeout(finalize, 1200);
        };
    };

    return (
        <div className="attendance-table-container">
            <div className="table-header">
                <div className="table-title">
                    <i className="fas fa-table"></i>
                    Attendance Records
                </div>
                <div className="table-actions">
                    <div className="export-dropdown">
                        <button className="btn btn-export">
                            <i className="fas fa-download"></i>
                            Export
                        </button>
                        <div className="dropdown-menu">
                            <button onClick={() => handleExportClick('csv')} className="dropdown-item">
                                <i className="fas fa-file-csv"></i>
                                <span>Export as CSV</span>
                            </button>
                            <button onClick={() => handleExportClick('pdf')} className="dropdown-item">
                                <i className="fas fa-file-pdf"></i>
                                <span>Export as PDF</span>
                            </button>
                            <button onClick={() => handleExportClick('summary')} className="dropdown-item">
                                <i className="fas fa-file-invoice"></i>
                                <span>Export Summary as CSV (per staff)</span>
                            </button>
                            <button onClick={() => handleExportClick('summary_pdf')} className="dropdown-item">
                                <i className="fas fa-file-invoice"></i>
                                <span>Export Summary as PDF (per staff)</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="table-loading">
                    <LoadingSpinner />
                    <p>Loading attendance records...</p>
                </div>
            ) : attendanceRows.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">
                        <i className="fas fa-clipboard-list"></i>
                    </div>
                    <h3>No Attendance Records Found</h3>
                    <p>No attendance records match your current filters. Try adjusting your search criteria or date range.</p>
                </div>
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    {userType !== 'staff' && <th><i className="fas fa-user"></i> Staff Member</th>}
                                    <th><i className="fas fa-calendar"></i> Date</th>
                                    {userType !== 'staff' && <th><i className="fas fa-sitemap"></i> Organization</th>}
                                    <th><i className="fas fa-building"></i> Premises</th>
                                    <th><i className="fas fa-map-marker-alt"></i> Type</th>
                                    <th><i className="fas fa-business-time"></i> Shift</th>
                                    <th><i className="fas fa-sign-in-alt"></i> First Check In</th>
                                    <th><i className="fas fa-sign-out-alt"></i> Last Check Out</th>
                                    <th><i className="fas fa-hourglass-half"></i> Total Hours</th>
                                    <th><i className="fas fa-info-circle"></i> Status</th>
                                    <th><i className="fas fa-list"></i> Entries</th>
                                    <th><i className="fas fa-cog"></i> Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attendanceRows.map((record) => {
                                    let earliestCheckIn = null;
                                    let latestCheckOut = null;
                                    let totalMinutes = 0;

                                    (record.entries || []).forEach((entry) => {
                                        if (entry.sign_in_time && (!earliestCheckIn || entry.sign_in_time < earliestCheckIn)) {
                                            earliestCheckIn = entry.sign_in_time;
                                        }
                                        if (entry.sign_out_time && (!latestCheckOut || entry.sign_out_time > latestCheckOut)) {
                                            latestCheckOut = entry.sign_out_time;
                                        }
                                        const minutes = getAttendanceDurationMinutes(entry.sign_in_time, entry.sign_out_time);
                                        if (minutes !== null) totalMinutes += minutes;
                                    });

                                    const totalHours = Math.floor(totalMinutes / 60);
                                    const remainingMinutes = totalMinutes % 60;
                                    const totalWorkingHours = totalMinutes > 0 ? `${totalHours}h ${remainingMinutes}m` : '-';
                                    const firstEntryType = record.entries?.[0]?.attendance_type || record.attendance_type;
                                    const attendanceType = getAttendanceTypeDisplay(firstEntryType);

                                    return (
                                        <tr key={`${record.user_id || 'nouser'}_${record.date || 'nodate'}`} className="table-row">
                                            {userType !== 'staff' && (
                                                <td>
                                                    <div className="staff-info">
                                                        <div className="staff-avatar">
                                                            {record.staff_passport ? (
                                                                <img
                                                                    src={resolveMediaUrl(record.staff_passport)}
                                                                    alt={record.staff_name || record.username || 'Staff'}
                                                                    onError={(e) => {
                                                                        e.currentTarget.style.display = 'none';
                                                                        const fallback = e.currentTarget.nextElementSibling;
                                                                        if (fallback) fallback.style.display = 'inline-block';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <i className="fas fa-user-circle" style={{ display: record.staff_passport ? 'none' : 'inline-block' }}></i>
                                                        </div>
                                                        <div className="staff-details">
                                                            <strong>{record.staff_name || record.username}</strong>
                                                            <small>{record.staff_id || 'N/A'}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                            )}
                                            <td>
                                                <div className="date-cell">
                                                    <strong>{formatDate(record.date)}</strong>
                                                </div>
                                            </td>
                                            {userType !== 'staff' && (
                                                <td>
                                                    <span className="premises-badge">
                                                        {record.organization_name || '-'}
                                                    </span>
                                                </td>
                                            )}
                                            <td>
                                                <span className="premises-badge">
                                                    {record.premises_name || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`attendance-type ${attendanceType.className}`}>
                                                    <i className={`fas ${attendanceType.className === 'offsite' ? 'fa-location-dot' : 'fa-building'}`}></i>
                                                    {attendanceType.label}
                                                </span>
                                            </td>
                                            <td>
                                                {record.shift_name ? (
                                                    <div className="shift-info">
                                                        <div className="shift-name">
                                                            <i className="fas fa-briefcase"></i>
                                                            <strong>{record.shift_name}</strong>
                                                        </div>
                                                        <div className="shift-times">
                                                            <span className="shift-time">
                                                                {formatShiftTime(record.shift_start_time)} - {formatShiftTime(record.shift_end_time)}
                                                            </span>
                                                        </div>
                                                        {formatTimeDifference(record.time_difference)}
                                                    </div>
                                                ) : (
                                                    <span className="no-shift">
                                                        <i className="fas fa-minus-circle"></i>
                                                        No Shift Assigned
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`time-badge ${earliestCheckIn ? 'time-present' : 'time-absent'}`}>
                                                    <i className={`fas ${earliestCheckIn ? 'fa-check' : 'fa-minus'}`}></i>
                                                    {formatTime(earliestCheckIn)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`time-badge ${latestCheckOut ? 'time-present' : 'time-pending'}`}>
                                                    <i className={`fas ${latestCheckOut ? 'fa-check' : 'fa-clock'}`}></i>
                                                    {formatTime(latestCheckOut)}
                                                </span>
                                            </td>
                                            <td>
                                                <strong className="working-hours">{totalWorkingHours}</strong>
                                            </td>
                                            <td>{getStatusBadge(record.status, earliestCheckIn, latestCheckOut, record.actual_status)}</td>
                                            <td>
                                                <span className="entries-count">
                                                    <i className="fas fa-list"></i>
                                                    {record.entries?.length || 0} {(record.entries?.length || 0) === 1 ? 'Entry' : 'Entries'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="action-btn view"
                                                        onClick={() => handleViewDetails(record)}
                                                        title="View Details"
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                        View
                                                    </button>
                                                    {canReview && record.has_review_reason && (
                                                        <button
                                                            className="action-btn edit"
                                                            onClick={() => handleReviewClick(record)}
                                                            title="Review attendance reason"
                                                        >
                                                            <i className="fas fa-user-check"></i>
                                                            Review
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="table-footer">
                    <div className="pagination-info">
                        <i className="fas fa-info-circle"></i>
                        <span className="pagination-text">
                            {`Showing ${((pagination.current_page - 1) * pagination.per_page) + 1} to ${Math.min(pagination.current_page * pagination.per_page, pagination.total)} of ${pagination.total} records`}
                        </span>
                    </div>

                        <div className="pagination">
                            {renderPaginationButtons()}
                        </div>
                    </div>
                </>
            )}

            <style jsx>{`
        .attendance-table-container {
          background: white;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        .table-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1.5rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .table-title {
          font-size: 1.4rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }

        .table-actions {
          display: flex;
          gap: 0.8rem;
        }

        .btn-export {
          background: rgba(255, 255, 255, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.5);
          color: white;
          padding: 0.6rem 1.2rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-export:hover {
          background: rgba(255, 255, 255, 0.3);
          border-color: white;
        }

        .export-dropdown {
          position: relative;
        }

        .dropdown-menu {
          position: absolute;
          top: calc(100% + 0.5rem);
          right: 0;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          min-width: 200px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.3s ease;
          z-index: 100;
          overflow: hidden;
        }

        .export-dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          padding: 0.9rem 1.2rem;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          color: #2d3748;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .dropdown-item:hover {
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
        }

        .dropdown-item i {
          color: #667eea;
          font-size: 1.1rem;
        }

        .table-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem;
          gap: 1rem;
          color: #718096;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
        }

        .empty-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
        }

        .empty-icon i {
          font-size: 3rem;
          color: #667eea;
        }

        .empty-state h3 {
          color: #2d3748;
          margin-bottom: 0.8rem;
          font-size: 1.5rem;
        }

        .empty-state p {
          color: #718096;
          max-width: 500px;
        }

        .table-responsive {
          overflow-x: auto;
        }

        .table {
          width: 100%;
          border-collapse: collapse;
        }

        .table th {
          background: #f8f9fa;
          color: #2d3748;
          font-weight: 600;
          padding: 1.2rem 1rem;
          text-align: left;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
          font-size: 0.9rem;
        }

        .table th i {
          margin-right: 0.5rem;
          color: #667eea;
        }

        .table td {
          padding: 1.2rem 1rem;
          border-bottom: 1px solid #f1f3f5;
          vertical-align: middle;
        }

        .table-row {
          transition: all 0.2s ease;
        }

        .table-row:hover {
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%);
        }

        .staff-info {
          display: flex;
          align-items: center;
          gap: 0.8rem;
        }

        .staff-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.2rem;
        }

        .staff-avatar img {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          object-fit: cover;
          border: 1px solid #e2e8f0;
          display: inline-block;
          background: #fff;
        }

        .staff-details {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .staff-details strong {
          color: #2d3748;
          font-size: 0.95rem;
        }

        .staff-details small {
          color: #718096;
          font-size: 0.8rem;
        }

        .date-cell strong {
          color: #2d3748;
        }

        .premises-badge {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .time-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .time-badge i {
          font-size: 0.8rem;
        }

        .time-present {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .time-absent {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
          font-style: italic;
        }

        .time-pending {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .working-hours {
          color: #2d3748;
          font-size: 0.95rem;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .status-badge.present {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .status-badge.absent {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .status-badge.late {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .status-badge.on-leave {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
        }

        .status-badge.holiday {
          background: rgba(139, 92, 246, 0.15);
          color: #8b5cf6;
        }

        .status-badge.on-time {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .status-badge.within-margin {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .shift-info {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          min-width: 180px;
        }

        .shift-name {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #2d3748;
          font-size: 0.9rem;
        }

        .shift-name i {
          color: #667eea;
          font-size: 0.85rem;
        }

        .shift-name strong {
          font-weight: 600;
        }

        .shift-times {
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .shift-time {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .time-diff {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-top: 0.2rem;
        }

        .time-diff.late {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .time-diff.early {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .time-diff i {
          font-size: 0.7rem;
        }

        .no-shift {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 500;
          font-style: italic;
        }

        .attendance-type {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(107, 114, 128, 0.1);
          color: #4b5563;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .attendance-type.in-premises {
          background: rgba(16, 185, 129, 0.15);
          color: #047857;
        }

        .attendance-type.offsite {
          background: rgba(59, 130, 246, 0.15);
          color: #1d4ed8;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem 0.8rem;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }

        .action-btn.edit {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .action-btn.edit:hover {
          background: rgba(245, 158, 11, 0.2);
          transform: translateY(-2px);
        }

        .action-btn.delete {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .action-btn.delete:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: translateY(-2px);
        }

        .action-btn.view {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          padding: 0.6rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
        }

        .action-btn.view:hover {
          background: rgba(59, 130, 246, 0.2);
          transform: translateY(-2px);
        }

        .entries-count {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .table-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          background: #f8f9fa;
          border-top: 1px solid #e2e8f0;
        }

        .pagination-info {
          color: #718096;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .pagination-info i {
          color: #667eea;
        }

        .pagination-info strong {
          color: #2d3748;
          display: inline;
        }

        .pagination-text {
          display: inline;
          color: #718096;
          font-weight: 500;
          white-space: nowrap;
        }

        .pagination {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .pagination-btn {
          padding: 0.6rem 1rem;
          border: 2px solid #e2e8f0;
          background: white;
          color: #2d3748;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .pagination-btn:hover:not(.active):not(:disabled) {
          border-color: #667eea;
          color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .pagination-btn.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-color: transparent;
        }

        .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .pagination-ellipsis {
          color: #718096;
          padding: 0 0.5rem;
        }

        @media (max-width: 768px) {
          .table-header {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }

          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .table {
            min-width: 1100px;
          }

          .shift-info {
            min-width: 150px;
          }

          .table th,
          .table td {
            padding: 0.8rem 0.6rem;
            font-size: 0.85rem;
          }

          .action-buttons {
            flex-direction: column;
          }

          .table-footer {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }

          .pagination {
            justify-content: center;
          }
        }
      `}</style>

            {/* Details Modal */}
            <Modal
                show={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                title="Attendance Details"
                size="medium"
            >
                {selectedRecord && (
                    <div className="attendance-details">
                        <div className="modal-actions">
                            <button className="action-btn-modal pdf" onClick={handleExportDetailsPDF}>
                                <i className="fas fa-file-pdf"></i>
                                Print as PDF
                            </button>
                        </div>
                        <div className="detail-header">
                            <h3>{selectedRecord.staff_name || selectedRecord.username}</h3>
                            <p className="detail-date">
                                <i className="fas fa-calendar"></i>
                                {formatDate(selectedRecord.date)}
                            </p>
                            <p className="detail-premises">
                                <i className="fas fa-building"></i>
                                {selectedRecord.premises_name}
                            </p>
                            {selectedRecord.shift_name && (
                                <p className="detail-shift">
                                    <i className="fas fa-briefcase"></i>
                                    {selectedRecord.shift_name} ({formatShiftTime(selectedRecord.shift_start_time)} - {formatShiftTime(selectedRecord.shift_end_time)})
                                </p>
                            )}
                        </div>

                        <div className="entries-list">
                            <h4>
                                <i className="fas fa-list"></i>
                                Check-in/Check-out Entries ({selectedRecord.entries?.length || 0})
                            </h4>
                            {(selectedRecord.entries || [])
                                .slice()
                                .sort((a, b) => String(a.sign_in_time || '').localeCompare(String(b.sign_in_time || '')))
                                .map((entry, idx) => {
                                const workHours = calculateWorkingHours(entry.sign_in_time, entry.sign_out_time);
                                return (
                                    <div key={entry.id} className="entry-item">
                                        <div className="entry-number">#{idx + 1}</div>
                                        <div className="entry-details">
                                            <div className="entry-row">
                                                <span className="entry-label">
                                                    <i className="fas fa-sign-in-alt"></i>
                                                    Check In:
                                                </span>
                                                <span className="entry-value">{formatTime(entry.sign_in_time)}</span>
                                            </div>
                                            <div className="entry-row">
                                                <span className="entry-label">
                                                    <i className="fas fa-sign-out-alt"></i>
                                                    Check Out:
                                                </span>
                                                <span className="entry-value">{formatTime(entry.sign_out_time)}</span>
                                            </div>
                                            <div className="entry-row">
                                                <span className="entry-label">
                                                    <i className="fas fa-hourglass-half"></i>
                                                    Duration:
                                                </span>
                                                <span className="entry-value entry-duration">{workHours}</span>
                                            </div>
                                            <div className="entry-row">
                                                <span className="entry-label">
                                                    <i className="fas fa-tag"></i>
                                                    Type:
                                                </span>
                                                <span className="entry-value">
                                                    {entry.attendance_type === 'face_recognition' ? 'Face Recognition' :
                                                     entry.attendance_type === 'manual' ? 'Manual Entry' :
                                                     entry.attendance_type === 'offsite' ? 'Offsite' :
                                                     entry.attendance_type || 'Standard'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <style jsx>{`
                            .attendance-details {
                                padding: 1rem;
                            }
                            .modal-actions {
                                display: flex;
                                justify-content: flex-end;
                                margin-bottom: 0.8rem;
                            }
                            .action-btn-modal.pdf {
                                background: rgba(239, 68, 68, 0.1);
                                color: #dc2626;
                                border: 1px solid rgba(239, 68, 68, 0.35);
                                border-radius: 8px;
                                padding: 0.5rem 0.85rem;
                                font-weight: 600;
                                cursor: pointer;
                                display: inline-flex;
                                align-items: center;
                                gap: 0.4rem;
                            }

                            .detail-header {
                                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                padding: 1.5rem;
                                border-radius: 12px;
                                color: white;
                                margin-bottom: 1.5rem;
                            }

                            .detail-header h3 {
                                margin: 0 0 0.8rem 0;
                                font-size: 1.5rem;
                            }

                            .detail-header p {
                                margin: 0.4rem 0;
                                display: flex;
                                align-items: center;
                                gap: 0.5rem;
                                font-size: 0.95rem;
                            }

                            .entries-list h4 {
                                color: #2d3748;
                                margin-bottom: 1rem;
                                display: flex;
                                align-items: center;
                                gap: 0.5rem;
                            }

                            .entry-item {
                                background: #f8f9fa;
                                border-radius: 12px;
                                padding: 1rem;
                                margin-bottom: 1rem;
                                display: flex;
                                gap: 1rem;
                                border-left: 4px solid #667eea;
                            }

                            .entry-number {
                                width: 40px;
                                height: 40px;
                                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                color: white;
                                font-weight: bold;
                                flex-shrink: 0;
                            }

                            .entry-details {
                                flex: 1;
                            }

                            .entry-row {
                                display: flex;
                                justify-content: space-between;
                                padding: 0.5rem 0;
                                border-bottom: 1px solid #e2e8f0;
                            }

                            .entry-row:last-child {
                                border-bottom: none;
                            }

                            .entry-label {
                                color: #718096;
                                font-weight: 500;
                                display: flex;
                                align-items: center;
                                gap: 0.5rem;
                            }

                            .entry-label i {
                                color: #667eea;
                            }

                            .entry-value {
                                color: #2d3748;
                                font-weight: 600;
                            }

                            .entry-duration {
                                color: #667eea;
                            }
                        `}</style>
                    </div>
                )}
            </Modal>

            <Modal
                show={showReviewModal}
                onClose={() => setShowReviewModal(false)}
                title="Review Attendance"
                size="small"
            >
                {reviewRecord && (
                    <div style={{ padding: '0.5rem', display: 'grid', gap: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc' }}>
                            <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {reviewRecord.staff_passport ? (
                                    <img
                                        src={resolveMediaUrl(reviewRecord.staff_passport)}
                                        alt={reviewRecord.staff_name || reviewRecord.username || 'Staff'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <i className="fas fa-user" style={{ color: '#64748b' }}></i>
                                )}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700, color: '#1e293b' }}>{reviewRecord.staff_name || reviewRecord.username || '-'}</div>
                                <div style={{ color: '#64748b', fontSize: '0.86rem' }}>Attendance Review</div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem 0.8rem' }}>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.55rem 0.7rem' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Username</div>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>{reviewRecord.username || '-'}</div>
                            </div>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.55rem 0.7rem' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Mobile Number</div>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>{reviewRecord.staff_mobile_number || reviewRecord.phone_number || reviewRecord.username || '-'}</div>
                            </div>
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.55rem 0.7rem', gridColumn: '1 / -1' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Date</div>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>{formatDate(reviewRecord.date)}</div>
                            </div>
                        </div>

                        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.65rem 0.75rem', background: '#fff' }}>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.35rem' }}>Reason</div>
                            <div style={{ color: '#334155', lineHeight: 1.45 }}>{reviewRecord.review_reason || 'Reason provided on attendance entry'}</div>
                        </div>

                        {reviewRecord.sign_in_time && (
                            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.55rem 0.7rem' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Sign In Time</div>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>{formatTime(reviewRecord.sign_in_time)}</div>
                            </div>
                        )}
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600, color: '#334155' }}>Sign Out Time</label>
                            <input
                                type="time"
                                className="form-input"
                                value={reviewForm.sign_out_time}
                                onChange={(e) => setReviewForm((prev) => ({ ...prev, sign_out_time: e.target.value }))}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                                type="checkbox"
                                id="review-next-day"
                                checked={reviewForm.next_day}
                                onChange={(e) => setReviewForm((prev) => ({ ...prev, next_day: e.target.checked }))}
                            />
                            <label htmlFor="review-next-day" style={{ fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                                Sign out time is on the next day (overnight shift)
                            </label>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600, color: '#334155' }}>Review Note (Optional)</label>
                            <textarea
                                className="form-input"
                                rows={3}
                                placeholder="Add review context for audit trail"
                                value={reviewForm.review_note}
                                onChange={(e) => setReviewForm((prev) => ({ ...prev, review_note: e.target.value }))}
                            />
                        </div>
                        <div style={{ marginTop: '0.2rem', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
                            <button className="btn btn-secondary" onClick={() => setShowReviewModal(false)} disabled={reviewSaving}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleReviewSubmit} disabled={reviewSaving}>
                                {reviewSaving ? 'Saving...' : 'Update Sign Out'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AttendanceTable;
